/**
 * Runtime Configuration Manager
 * Allows setting API URLs and other config at runtime without rebuilding
 */

export interface RuntimeConfig {
	apiUrl: string;
	environment: string;
	timeout?: number;
	enableDebugLogs?: boolean;
	/** API version: "v2" | "v3" | "v5" */
	apiVersion?: "v2" | "v3" | "v5";
}

// Default configuration - fallback values
const DEFAULT_CONFIG: RuntimeConfig = {
	apiUrl: "https://q5ramps.aws.moneygram.com",
	environment: "q5",
	timeout: 30000,
	enableDebugLogs: false,
	apiVersion: "v3",
};

// Environment-specific defaults
// Note: v3 API uses *ramps.aws.moneygram.com domains
const ENV_DEFAULTS: Record<string, Partial<RuntimeConfig>> = {
	// AWS subdomains - v3 API
	d5: { apiUrl: "https://d5ramps.aws.moneygram.com", environment: "d5" },
	d3: { apiUrl: "https://d3ramps.aws.moneygram.com", environment: "d3" },
	d2: { apiUrl: "https://d2ramps.aws.moneygram.com", environment: "d2" },
	q5: { apiUrl: "https://q5ramps.aws.moneygram.com", environment: "q5" },
	q3: { apiUrl: "https://q3ramps.aws.moneygram.com", environment: "q3" },
	q2: { apiUrl: "https://q2ramps.aws.moneygram.com", environment: "q2" },
	q1: { apiUrl: "https://q1ramps.moneygram.com", environment: "q1" },

	// Non-AWS domains - v3 API
	prod: {
		apiUrl: "https://ramps.moneygram.com",
		environment: "prod",
	},
	ext: {
		apiUrl: "https://extramps.moneygram.com",
		environment: "ext",
	},
};

declare global {
	interface Window {
		RAMPS_CONFIG?: Partial<RuntimeConfig>;
	}
}

/**
 * URL-based environment detection patterns
 */
const URL_ENVIRONMENT_PATTERNS = [
	// AWS subdomains - v3 API
	{ pattern: /^d5ramps\.aws\.moneygram\.com$/i, environment: "d5" },
	{ pattern: /^d3ramps\.aws\.moneygram\.com$/i, environment: "d3" },
	{ pattern: /^d2ramps\.aws\.moneygram\.com$/i, environment: "d2" },
	{ pattern: /^q5ramps\.aws\.moneygram\.com$/i, environment: "q5" },
	{ pattern: /^q3ramps\.aws\.moneygram\.com$/i, environment: "q3" },
	{ pattern: /^q2ramps\.aws\.moneygram\.com$/i, environment: "q2" },
	{ pattern: /^q1ramps\.moneygram\.com$/i, environment: "q1" },

	// Non-AWS domains - v3 API (production/external)
	{ pattern: /^ramps\.moneygram\.com$/i, environment: "prod" },
	{ pattern: /^extramps\.moneygram\.com$/i, environment: "ext" },

	// Development/localhost
	{ pattern: /^localhost(:\d+)?$/i, environment: "local" },
	{ pattern: /^127\.0\.0\.1(:\d+)?$/i, environment: "local" },
];

/**
 * Detects environment based on current URL hostname
 */
export function detectEnvironmentFromUrl(): string | null {
	if (typeof window === "undefined") {
		return null;
	}

	const hostname = window.location.hostname;

	for (const { pattern, environment } of URL_ENVIRONMENT_PATTERNS) {
		if (pattern.test(hostname)) {
			return environment;
		}
	}

	return null;
}

/**
 * Gets the runtime configuration by merging:
 * 1. Default config
 * 2. URL-based environment detection (NEW!)
 * 3. Environment-specific defaults
 * 4. Build-time environment variables
 * 5. Runtime window.RAMPS_CONFIG
 */
export function getRuntimeConfig(): RuntimeConfig {
	// Start with defaults
	let config = { ...DEFAULT_CONFIG };

	// Auto-detect environment from URL (highest priority for environment detection)
	const urlDetectedEnv = detectEnvironmentFromUrl();
	if (urlDetectedEnv && ENV_DEFAULTS[urlDetectedEnv]) {
		config = { ...config, ...ENV_DEFAULTS[urlDetectedEnv] };
	}

	// Apply environment-specific defaults based on build-time env (if no URL detection)
	const buildTimeEnv = import.meta.env.VITE_TARGET_ENV;
	if (!urlDetectedEnv && buildTimeEnv && ENV_DEFAULTS[buildTimeEnv]) {
		config = { ...config, ...ENV_DEFAULTS[buildTimeEnv] };
	}

	// Override with build-time environment variables
	if (import.meta.env.VITE_RAMPS_API_URL) {
		config.apiUrl = import.meta.env.VITE_RAMPS_API_URL;
	}

	// Apply additional environment variables
	if (import.meta.env.VITE_API_TIMEOUT) {
		config.timeout = Number(import.meta.env.VITE_API_TIMEOUT);
	}

	if (import.meta.env.VITE_ENABLE_DEBUG_LOGS) {
		config.enableDebugLogs = import.meta.env.VITE_ENABLE_DEBUG_LOGS === "true";
	}

	if (import.meta.env.VITE_API_VERSION) {
		config.apiVersion = import.meta.env.VITE_API_VERSION;
	}

	// Override with runtime configuration (highest priority for overrides)
	if (typeof window !== "undefined" && window.RAMPS_CONFIG) {
		config = { ...config, ...window.RAMPS_CONFIG };
	}

	// Use proxy in development if no custom URL is set
	if (
		import.meta.env.DEV &&
		!window.RAMPS_CONFIG?.apiUrl &&
		!import.meta.env.VITE_RAMPS_API_URL
	) {
		config.apiUrl = "/api";
	}

	return config;
}

/**
 * Sets runtime configuration
 * Useful for testing or dynamic environment switching
 */
export function setRuntimeConfig(config: Partial<RuntimeConfig>): void {
	if (typeof window !== "undefined") {
		window.RAMPS_CONFIG = { ...window.RAMPS_CONFIG, ...config };
	}
}

/**
 * Gets the current environment name
 */
export function getCurrentEnvironment(): string {
	return getRuntimeConfig().environment;
}

/**
 * Checks if we're running in a specific environment
 */
export function isEnvironment(env: string): boolean {
	return getCurrentEnvironment() === env;
}

/**
 * Debug helper to log current configuration
 */
export function logCurrentConfig(): void {
	const config = getRuntimeConfig();
	const urlDetectedEnv = detectEnvironmentFromUrl();

	console.log("🔧 Current Ramps Configuration:", {
		...config,
		source: {
			currentUrl: typeof window !== "undefined" ? window.location.href : "N/A",
			hostname:
				typeof window !== "undefined" ? window.location.hostname : "N/A",
			urlDetectedEnv,
			buildTimeEnv: import.meta.env.VITE_TARGET_ENV,
			buildTimeApiUrl: import.meta.env.VITE_RAMPS_API_URL,
			runtimeConfig:
				typeof window !== "undefined" ? window.RAMPS_CONFIG : "N/A",
		},
	});
}

/**
 * Test URL detection with a custom hostname (for debugging)
 */
export function testUrlDetection(hostname: string): string | null {
	for (const { pattern, environment } of URL_ENVIRONMENT_PATTERNS) {
		if (pattern.test(hostname)) {
			return environment;
		}
	}
	return null;
}
